import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Header from './Components/Header';
import Footer from './Components/Footer';

const normalizeListing = (listing = {}) => ({
  ...listing,
  id: listing.id ?? listing._id ?? listing.slug ?? listing.uuid ?? listing.businessId,
  name: listing.name ?? listing.businessName ?? listing.companyName ?? listing.title ?? listing.displayName,
  title: listing.title ?? listing.name ?? listing.businessName ?? listing.companyName,
  description: listing.description ?? listing.about ?? listing.bio ?? '',
  price: Number(listing.price ?? listing.startingPrice ?? listing.minPrice ?? 0),
  rating: Number(listing.rating ?? listing.averageRating ?? 0),
  services: Array.isArray(listing.services) ? listing.services : (Array.isArray(listing.serviceTypes) ? listing.serviceTypes : []),
  tags: Array.isArray(listing.tags) ? listing.tags : (Array.isArray(listing.categories) ? listing.categories : []),
  image: listing.image ?? listing.images?.[0] ?? listing.photo ?? '',
  isVerified: Boolean(listing.isVerified ?? listing.verified ?? listing.is_verified)
});

const extractListing = (data) => {
  if (data && !Array.isArray(data) && typeof data === 'object') {
    if (data.listing) return data.listing;
    if (data.data && !Array.isArray(data.data)) return data.data;
    if (data.result && !Array.isArray(data.result)) return data.result;
  }
  return data;
};

export default function Profile() {
  const { id } = useParams();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(Boolean(id));
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [bookingConfirmed, setBookingConfirmed] = useState(false);
  const [selectedService, setSelectedService] = useState('Residential Lockout');
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    date: '',
    time: '12:00 PM',
    notes: ''
  });

  useEffect(() => {
    if (!id) {
      setListing(null);
      setLoading(false);
      return;
    }

    let isMounted = true;

    const fetchListing = async () => {
      setLoading(true);
      setError('');

      const backendUrl = process.env.REACT_APP_BACKEND_URL || 'http://localhost:3005';
      const candidateUrls = [
        `${backendUrl}/api/listings/${encodeURIComponent(id)}`,
        `${backendUrl}/listings/${encodeURIComponent(id)}`
      ];

      try {
        let fetchedListing = null;

        for (const url of candidateUrls) {
          const res = await fetch(url);
          if (res.ok) {
            const data = await res.json();
            fetchedListing = normalizeListing(extractListing(data));
            break;
          }
        }

        if (isMounted) {
          if (fetchedListing) {
            setListing(fetchedListing);
          } else {
            setError('Unable to load profile details.');
          }
        }
      } catch (err) {
        if (isMounted) {
          console.error('Failed to fetch listing profile', err);
          setError('Unable to load profile details.');
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchListing();

    return () => {
      isMounted = false;
    };
  }, [id]);

  const profileName = listing?.name || listing?.title || 'Professional Locksmith';
  const profileSubtitle = listing?.description || 'Licensed & Verified Locksmith';
  const profileImage = listing?.image || 'https://lh3.googleusercontent.com/aida-public/AB6AXuBYuUkyyoIsWnbfemHT_fnWq762eTWEMwYJNhEVM5BRUNrNwCQpoa46TxA5rA_0ppIZVhbGCtQqpIrNlA5lmnxIs4Tqbh7UPT6zb6Ny2z4JVrt5n9Q1GZc1ckZyYu_b3OeORHl2b1qOrxiG4uJw0r57TU2Qfg_e5nNY1Sjnp540HJSAhlJLj8HzKEWRvnWvXjLQCUhIwrvjRFybqnLe93aLO-Gbk2DRSpsyQonOfvh0ERtJKhH0bOHq';
  const profileRating = listing?.rating ? listing.rating.toFixed(1) : '4.9';
  const profileServices = Array.isArray(listing?.services) && listing.services.length > 0
    ? listing.services
    : ['Residential Lockout', 'Lock Rekeying', 'Smart Lock Install', 'Commercial Audit'];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleBookClick = (serviceName) => {
    if (serviceName && typeof serviceName === 'string') {
      setSelectedService(serviceName);
    }
    setBookingConfirmed(false);
    setIsModalOpen(true);
  };

  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.date) {
      alert('Please fill out all required fields.');
      return;
    }
    setBookingConfirmed(true);
  };

  if (loading) {
    return (
      <div className="bg-background text-on-background font-body-md pt-16 min-h-screen flex items-center justify-center">
        <Header />
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-on-surface-variant">Loading profile details...</p>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-background text-on-background font-body-md pt-16 selection:bg-secondary-fixed selection:text-on-secondary-fixed">
      <Header />

      <main className="max-w-container-max mx-auto px-margin-mobile md:px-margin-desktop py-8 lg:py-12">
        {/* Hero Section */}
        <section className="grid grid-cols-1 lg:grid-cols-12 gap-gutter mb-12">
          <div className="lg:col-span-8 bg-surface-container-lowest rounded-xl border border-outline-variant p-6 lg:p-10 flex flex-col md:flex-row gap-8 items-start md:items-center relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 opacity-5 pointer-events-none">
              <span className="material-symbols-outlined text-[128px]">verified_user</span>
            </div>
            <div className="relative">
              <div className="w-32 h-32 md:w-48 md:h-48 rounded-2xl overflow-hidden border-4 border-surface-container shadow-lg">
                <img
                  className="w-full h-full object-cover"
                  alt={profileName}
                  src={profileImage}
                />
              </div>
              <div className="absolute -bottom-3 -right-3 bg-secondary text-on-secondary px-3 py-1 rounded-full text-xs font-bold shadow-md flex items-center gap-1">
                <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>
                  verified
                </span>
                VERIFIED PRO
              </div>
            </div>
            <div className="flex-1 space-y-4">
              <div>
                <div className="flex flex-wrap items-center gap-3 mb-2">
                  <span className="text-[12px] font-bold bg-primary-fixed text-on-primary-fixed px-3 py-0.5 rounded-full">
                    Licensed & Insured
                  </span>
                  <span className="text-[12px] font-bold bg-surface-container-high text-primary px-3 py-0.5 rounded-full flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">shield_person</span>
                    Background Checked
                  </span>
                </div>
                <h1 className="font-headline-xl text-headline-lg-mobile md:text-headline-xl text-primary leading-tight">
                  {profileName}
                </h1>
                <p className="font-body-lg text-body-lg text-on-surface-variant">
                  {profileSubtitle}
                </p>
              </div>
              <div className="flex items-center gap-6">
                <div className="flex items-center gap-1">
                  <span className="material-symbols-outlined text-secondary-container" style={{ fontVariationSettings: "'FILL' 1" }}>
                    star
                  </span>
                  <span className="font-bold text-lg">{profileRating}</span>
                  <span className="text-on-surface-variant text-sm">(482 Reviews)</span>
                </div>
                <div className="h-4 w-px bg-outline-variant"></div>
                <div className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-primary">schedule</span>
                  <span className="text-sm font-semibold">
                    Fast Response: <span className="text-secondary-container">~20 mins</span>
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Sidebar */}
          <div className="lg:col-span-4 space-y-gutter">
            <div className="bg-primary text-on-primary rounded-xl p-8 shadow-lg flex flex-col gap-6">
              <h3 className="font-headline-md text-headline-md text-white">Service Inquiry</h3>
              <div className="space-y-4">
                <a
                  href="tel:888555LOCK"
                  className="w-full py-4 bg-secondary hover:brightness-110 text-on-secondary font-bold rounded-lg transition-all flex items-center justify-center gap-3 shadow-md active:scale-95 text-center"
                >
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                    call
                  </span>
                  CALL (888) 555-LOCK
                </a>
                <button
                  onClick={() => handleBookClick('General Consultation')}
                  className="w-full py-4 border-2 border-on-primary/30 hover:border-on-primary hover:bg-on-primary/10 text-on-primary font-bold rounded-lg transition-all flex items-center justify-center gap-3 active:scale-95"
                >
                  <span className="material-symbols-outlined">calendar_today</span>
                  BOOK APPOINTMENT
                </button>
              </div>
              <div className="pt-4 border-t border-on-primary/10 flex items-center gap-3 justify-center text-sm opacity-80">
                <span className="material-symbols-outlined text-secondary-container" style={{ fontVariationSettings: "'FILL' 1" }}>
                  security
                </span>
                Encrypted & Protected Payment
              </div>
            </div>
          </div>
        </section>

        {/* Main Content Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-gutter">
          {/* Left Column: Details */}
          <div className="lg:col-span-8 space-y-12">
            {/* About Section */}
            <section>
              <div className="mb-6">
                <h2 className="font-headline-lg text-headline-lg text-primary">About the Pro</h2>
                <div className="border-b-4 border-b-secondary-container w-[60px] mt-2"></div>
              </div>
              <div className="bg-surface-container-low rounded-xl p-8 border border-outline-variant">
                <p className="font-body-lg text-body-lg text-on-surface-variant leading-relaxed">
                  With over 15 years of dedicated service in high-security environments and a background in military physical security, Master Sgt. David Miller brings institutional-grade reliability to your home or business. Miller Professional Locksmiths specializes in advanced electronic entry systems, traditional master keying, and emergency lockout services.
                </p>
                <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="flex flex-col items-center p-4 bg-surface-container-lowest rounded-lg text-center border border-outline-variant">
                    <span className="material-symbols-outlined text-primary mb-2 text-[32px]">military_tech</span>
                    <span className="font-bold text-sm text-primary">Military Background</span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-surface-container-lowest rounded-lg text-center border border-outline-variant">
                    <span className="material-symbols-outlined text-primary mb-2 text-[32px]">workspace_premium</span>
                    <span className="font-bold text-sm text-primary">GSA Certified</span>
                  </div>
                  <div className="flex flex-col items-center p-4 bg-surface-container-lowest rounded-lg text-center border border-outline-variant">
                    <span className="material-symbols-outlined text-primary mb-2 text-[32px]">support_agent</span>
                    <span className="font-bold text-sm text-primary">24/7 Availability</span>
                  </div>
                </div>
              </div>
            </section>

            {/* Services & Pricing */}
            <section>
              <div className="mb-6">
                <h2 className="font-headline-lg text-headline-lg text-primary">Services & Pricing</h2>
                <div className="border-b-4 border-b-secondary-container w-[60px] mt-2"></div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profileServices.map((service, idx) => {
                  const serviceData = [
                    { title: 'Residential Lockout', desc: 'Rapid house entry', price: 'From $75', icon: 'home' },
                    { title: 'Lock Rekeying', desc: 'Per cylinder service', price: 'From $25', icon: 'key' },
                    { title: 'Smart Lock Install', desc: 'Config & Setup', price: 'From $120', icon: 'sensor_door' },
                    { title: 'Commercial Audit', desc: 'Full security review', price: 'Contact', icon: 'business_center' }
                  ];
                  const fallback = serviceData[idx % serviceData.length];
                  const title = typeof service === 'string' ? service : service.title;
                  const desc = typeof service === 'string' ? fallback.desc : service.desc;
                  const price = typeof service === 'string' ? fallback.price : service.price;
                  const icon = typeof service === 'string' ? fallback.icon : service.icon;

                  return (
                    <div
                      key={idx}
                      onClick={() => handleBookClick(title)}
                      className="group bg-surface-container-lowest border border-outline-variant rounded-xl p-6 flex justify-between items-center cursor-pointer transition-all duration-300 hover:-translate-y-1 hover:shadow-md"
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-primary-fixed rounded-lg flex items-center justify-center text-on-primary-fixed">
                          <span className="material-symbols-outlined">{icon}</span>
                        </div>
                        <div>
                          <h4 className="font-bold text-primary group-hover:text-secondary transition-colors">
                            {title}
                          </h4>
                          <p className="text-sm text-on-surface-variant">{desc}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="font-headline-md text-secondary-container font-bold text-[20px]">{price}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* Map Section */}
            <section>
              <div className="mb-6">
                <h2 className="font-headline-lg text-headline-lg text-primary">Service Area Map</h2>
                <div className="border-b-4 border-b-secondary-container w-[60px] mt-2"></div>
              </div>
              <div className="rounded-xl overflow-hidden border border-outline-variant h-96 relative">
                <div
                  className="absolute inset-0 bg-cover bg-center"
                  style={{
                    backgroundImage: `url('https://lh3.googleusercontent.com/aida-public/AB6AXuCsnZZKBPYfIcvb_Dem47MuTJ3Y9OK_XszzXoeqrXHQYRJNDG0y_iUq-jgYNVswB_ssIMlv6EsXZFVzeKQ9NKjE3yf5fDjg4Vwy3VnuWdndFC8F83c_QOv1yoTLbZfaG_g-CMbtfK-65ApD7eTZl1CJYqROhDZwcQ306FV8K-KngpyEu5GlvBNJ-RW1EeeElz1lyq4ZKMf3a9HTDqY9cYMus8b62oosqG65_0Z28fCtykVT1RDiAVer')`
                  }}
                ></div>
                <div className="absolute bottom-6 left-6 bg-white p-4 rounded-lg shadow-lg border border-outline-variant max-w-xs">
                  <div className="flex items-center gap-2 mb-2 font-bold text-primary">
                    <span className="material-symbols-outlined">location_on</span>
                    Serving Greater Austin Area
                  </div>
                  <p className="text-sm text-on-surface-variant">
                    Covers 50-mile radius from Downtown. No extra travel fee within city limits.
                  </p>
                </div>
              </div>
            </section>

            {/* Reviews */}
            <section>
              <div className="mb-6 flex justify-between items-end">
                <div>
                  <h2 className="font-headline-lg text-headline-lg text-primary">Customer Reviews</h2>
                  <div className="border-b-4 border-b-secondary-container w-[60px] mt-2"></div>
                </div>
                <button
                  onClick={() => alert('Review system is a placeholder in this demo')}
                  className="text-primary font-bold hover:underline flex items-center gap-1"
                >
                  Write a Review <span className="material-symbols-outlined">rate_review</span>
                </button>
              </div>
              <div className="space-y-6">
                <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-primary rounded-full flex items-center justify-center text-on-primary font-bold">
                        JD
                      </div>
                      <div>
                        <h5 className="font-bold text-primary">John D.</h5>
                        <p className="text-xs text-on-surface-variant">2 days ago • Verified Service</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 text-secondary-container">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                          star
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-on-surface-variant italic leading-relaxed">
                    "Locked myself out at 2 AM. David arrived in exactly 15 minutes as promised. Extremely professional and didn't even have to drill the lock. Worth every penny."
                  </p>
                </div>

                <div className="bg-surface-container-low rounded-xl p-6 border border-outline-variant">
                  <div className="flex justify-between items-start mb-4">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-secondary-container rounded-full flex items-center justify-center text-on-secondary font-bold">
                        SM
                      </div>
                      <div>
                        <h5 className="font-bold text-primary">Sarah M.</h5>
                        <p className="text-xs text-on-surface-variant">1 week ago • Verified Service</p>
                      </div>
                    </div>
                    <div className="flex gap-0.5 text-secondary-container">
                      {[...Array(5)].map((_, i) => (
                        <span key={i} className="material-symbols-outlined text-[20px]" style={{ fontVariationSettings: "'FILL' 1" }}>
                          star
                        </span>
                      ))}
                    </div>
                  </div>
                  <p className="text-on-surface-variant italic leading-relaxed">
                    "Excellent work installing our new smart lock system. David was very patient in showing us how the app works and ensured our wifi connection was secure. Very thorough."
                  </p>
                </div>
              </div>
            </section>
          </div>

          {/* Right Column: Sidebar Badges & Recent Projects */}
          <div className="lg:col-span-4 space-y-gutter">
            <div className="bg-surface-container-highest rounded-xl p-8 space-y-6">
              <h3 className="font-bold text-primary uppercase tracking-widest text-sm">Security Qualifications</h3>
              <div className="flex items-start gap-4">
                <div className="bg-primary text-on-primary p-2 rounded-lg shrink-0">
                  <span className="material-symbols-outlined">policy</span>
                </div>
                <div>
                  <h4 className="font-bold text-primary">Certified Registered</h4>
                  <p className="text-sm text-on-surface-variant">Official registry of Texas locksmiths. License #TX-998271</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-primary text-on-primary p-2 rounded-lg shrink-0">
                  <span className="material-symbols-outlined">health_and_safety</span>
                </div>
                <div>
                  <h4 className="font-bold text-primary">$2M Liability Insurance</h4>
                  <p className="text-sm text-on-surface-variant">
                    Fully bonded and insured for all residential and commercial projects.
                  </p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="bg-primary text-on-primary p-2 rounded-lg shrink-0">
                  <span className="material-symbols-outlined">fingerprint</span>
                </div>
                <div>
                  <h4 className="font-bold text-primary">Background Cleared</h4>
                  <p className="text-sm text-on-surface-variant">
                    Biometric identity verified and criminal history check cleared for 2024.
                  </p>
                </div>
              </div>
            </div>

            {/* Recent Work Images */}
            <div className="bg-surface-container-lowest border border-outline-variant rounded-xl p-6">
              <h3 className="font-bold text-primary mb-4">Recent Projects</h3>
              <div className="grid grid-cols-2 gap-2">
                {[
                  'https://lh3.googleusercontent.com/aida-public/AB6AXuCAMewWYDmGwPBLntkscqQ9xrXUyCeAgV6gU2dKGswf_5vU-GZ5MS_JCAYb38j9KuZ3lz3hR937kUAeKt1Zx4TFK9lYj9f6zPyp2ZkGKKai1ubAB-S4bW7jNgdAMtAyKM2aoVKlmtZBK-j3-mROiiCgYsMflqMzLa8jazIk5R5Z7brfyD0nI8DaJb2f3UsCsSn9dA12UsndTMhdhF7eFNiVhb6qkp_W5DLblD6Am2oZ1gv_qbBtgsiu',
                  'https://lh3.googleusercontent.com/aida-public/AB6AXuAzRVYzu9B43ix6pmhN1k_U8vVhJmXmUBPc7ZOJ7w_s0THXRnIbyR2MZLanIZR59cswIixV61148hM67HqMqyB53mHJizAh041rGkFsOY_xCVJjgOKdleuxEKkXWJgrihsKM_JCRXaREHDxSnibU79q7wzehvRA6hz5fo4OmO-IqWTDFvSuaLBWhrzteizRFmO8csip8gzx5cxQeIAwj5eV2D5B9zDCaN8tRZd-eeDLfDxeoJGMGfxg',
                  'https://lh3.googleusercontent.com/aida-public/AB6AXuAubdYsgie6LNb5pD2eS3R3K_Aqw4B9L2ruLwMKcankQgixE651MApbglXU5lryKjhPOUC3VZyEwFehI0HOmuPQoEIk4PNDEdhzkHDmQPK9w-zpOSW9knu2nnxLKrBhVpa3gnImBD49QNsx-xEayZwGR4Zr3DK7GHHukna94xQS860B9Pd6ayFqw1f9mQ07lHJsQgFK3H1n9-dc-x7A8j_aNtEa_cjVW0SCFh5XNwCTrAbCIJai0rai',
                  'https://lh3.googleusercontent.com/aida-public/AB6AXuAqzOUmrBmgVq2sk2HstUCQ6KHPIlZxbnkFwvs45enj2gvu6evsciZvDtOl6sK_CNCSSi8tF1SxAtacPFHF0fKNP_FgH6eqDs7UCtrRRc-sjQUbQUdXPi9ZORHeBUEBnRtFzzLDYXQcljT62-arpbe6CMYcV6I7zKJLaAbCqYTUaBXd3RutQHdE1uTqXZeTjE3-nfFxGzhNZp30al0pLrLN2AcnMgiwyp6QDjdU96xJMCgHXMTjeyZM'
                ].map((url, idx) => (
                  <div key={idx} className="h-24 rounded-lg overflow-hidden border border-outline-variant shadow-sm hover:scale-105 transition-transform duration-300">
                    <img className="w-full h-full object-cover" alt={`Project ${idx + 1}`} src={url} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer />

      {/* Dynamic Booking Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-primary/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl relative border border-outline-variant animate-fadeIn">

            {/* Modal Header */}
            <div className="bg-primary text-on-primary p-6 flex justify-between items-center">
              <div>
                <h3 className="font-headline-md text-headline-md text-white">Book an Appointment</h3>
                <p className="text-sm text-surface-variant opacity-90">With {profileName}</p>
              </div>
              <button
                onClick={() => setIsModalOpen(false)}
                className="w-10 h-10 rounded-full flex items-center justify-center text-white hover:bg-white/10 active:scale-95 transition-all focus:outline-none"
              >
                <span className="material-symbols-outlined text-[24px]">close</span>
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6">
              {!bookingConfirmed ? (
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Service Selection */}
                  <div className="space-y-1">
                    <label className="font-label-md text-on-surface-variant block">Select Service</label>
                    <select
                      value={selectedService}
                      onChange={(e) => setSelectedService(e.target.value)}
                      className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg h-12 px-4 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    >
                      <option>Residential Lockout</option>
                      <option>Lock Rekeying</option>
                      <option>Smart Lock Install</option>
                      <option>Commercial Audit</option>
                      <option>General Consultation</option>
                    </select>
                  </div>

                  {/* Name & Phone */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="font-label-md text-on-surface-variant block">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="John Doe"
                        className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg h-12 px-4 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="font-label-md text-on-surface-variant block">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone"
                        required
                        value={formData.phone}
                        onChange={handleInputChange}
                        placeholder="(555) 000-0000"
                        className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg h-12 px-4 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                    </div>
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="font-label-md text-on-surface-variant block">Preferred Date *</label>
                      <input
                        type="date"
                        name="date"
                        required
                        value={formData.date}
                        onChange={handleInputChange}
                        className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg h-12 px-4 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="font-label-md text-on-surface-variant block">Preferred Time</label>
                      <select
                        name="time"
                        value={formData.time}
                        onChange={handleInputChange}
                        className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg h-12 px-4 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                      >
                        <option>09:00 AM</option>
                        <option>12:00 PM</option>
                        <option>03:00 PM</option>
                        <option>06:00 PM</option>
                      </select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div className="space-y-1">
                    <label className="font-label-md text-on-surface-variant block">Add Notes</label>
                    <textarea
                      name="notes"
                      rows="2"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Describe your request..."
                      className="w-full bg-surface-container-low border border-outline-variant/30 rounded-lg p-3 focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                    ></textarea>
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      className="w-full h-12 bg-secondary hover:brightness-110 text-on-secondary font-bold rounded-lg transition-all shadow-md active:scale-95"
                    >
                      Confirm Booking
                    </button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-8 space-y-4">
                  <span className="material-symbols-outlined text-[64px] text-green-600 animate-bounce">check_circle</span>
                  <h3 className="font-headline-md text-primary">Appointment Confirmed!</h3>
                  <div className="bg-surface-container-low p-4 rounded-xl border border-outline-variant/30 text-left max-w-sm mx-auto text-body-md text-on-surface-variant space-y-1">
                    <p><strong>Service:</strong> {selectedService}</p>
                    <p><strong>Name:</strong> {formData.name}</p>
                    <p><strong>Date:</strong> {formData.date}</p>
                    <p><strong>Time:</strong> {formData.time}</p>
                  </div>
                  <p className="text-sm text-on-surface-variant max-w-xs mx-auto">
                    A confirmation SMS details will be sent to <strong>{formData.phone}</strong> shortly.
                  </p>
                  <button
                    onClick={() => setIsModalOpen(false)}
                    className="mt-6 px-8 py-2.5 bg-primary text-on-primary rounded-lg font-label-md hover:brightness-110 active:scale-95 transition-all"
                  >
                    Done
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
